import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { Match, UpdateMatchRequest, MATCH_STATUSES } from '../../../core/models/match.model';
import { TournamentService } from '../../../core/services/tournament.service';

interface BracketRound {
  name: string;
  order: number;
  matches: Match[];
}

@Component({
  selector: 'app-bracket',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule],
  templateUrl: './bracket.component.html',
  styleUrl: './bracket.component.scss'
})
export class BracketComponent {
  @Input() matches: Match[] = [];
  @Input() branches: any[] = [];

  @Output() matchUpdated = new EventEmitter<Match>();

  editingMatchId: string | null = null;
  editForm: { homeScore: number | null; awayScore: number | null; status: string } = {
    homeScore: null,
    awayScore: null,
    status: ''
  };

  matchStatuses = MATCH_STATUSES;
  isSaving = false;

  private roundOrderMap: Record<string, number> = {
    'Round of 16': 0,
    'Knockout': 0,
    'Quarter-finals': 1,
    'Semi-finals': 2,
    'Final': 3
  };

  constructor(private tournamentService: TournamentService) {}

  get rounds(): BracketRound[] {
    // Filter out group stage matches
    const knockoutMatches = this.matches.filter(m => m.stage && m.stage !== 'Group Stage');

    // Group by stage
    const grouped = new Map<string, Match[]>();
    for (const m of knockoutMatches) {
      const stage = m.stage || 'Knockout';
      if (!grouped.has(stage)) grouped.set(stage, []);
      grouped.get(stage)!.push(m);
    }

    // Build rounds with order
    const rounds: BracketRound[] = [];
    for (const [name, matches] of grouped) {
      const order = this.roundOrderMap[name] ?? 0;
      const sorted = matches.sort((a, b) => (a.bracketPosition ?? 0) - (b.bracketPosition ?? 0));
      rounds.push({ name, order, matches: sorted });
    }

    // Sort rounds by order
    rounds.sort((a, b) => a.order - b.order);

    return rounds;
  }

  startEdit(match: Match) {
    if (this.isSaving) return;
    this.editingMatchId = match.id;
    this.editForm = {
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      status: match.status
    };
  }

  cancelEdit(event?: Event) {
    if (event) event.stopPropagation();
    this.editingMatchId = null;
  }

  saveEdit(match: Match, event?: Event) {
    if (event) event.stopPropagation();
    this.isSaving = true;

    const request: UpdateMatchRequest = {
      homeScore: this.editForm.homeScore,
      awayScore: this.editForm.awayScore,
      status: this.editForm.status
    };

    this.tournamentService.updateMatch(match.id, request).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.success && response.data) {
          this.editingMatchId = null;
          this.matchUpdated.emit(response.data);
        }
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating match:', error);
      }
    });
  }

  isWinner(match: Match, side: 'home' | 'away'): boolean {
    if (match.status !== 'completed' || match.homeScore === null || match.awayScore === null) {
      return false;
    }
    if (side === 'home') return match.homeScore > match.awayScore;
    return match.awayScore > match.homeScore;
  }

  getStatusLabel(match: Match): string {
    switch (match.status) {
      case 'draft': return 'Draft';
      case 'upcoming': return 'Upcoming';
      case 'live': return 'LIVE';
      case 'completed': return 'Completed';
      default: return match.status;
    }
  }
}
